import { Button, Modal } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Spinner } from 'components/ui';
import {
  type AtlasGqlAcknowledgement,
  useUpdateAcknowledgementMutation,
} from 'types/atlas-graphql';
import { useAuthContext } from 'utils/auth';

type Props = {
  showModal: boolean;
  setShowModal: (showModal: boolean) => void;
  acknowledgements: AtlasGqlAcknowledgement[];
};

export const AcknowledgementModal: React.FC<Props> = ({
  showModal,
  setShowModal,
  acknowledgements,
}: Props) => {
  const { t } = useTranslation();
  const { logout } = useAuthContext();
  const [currentAcknowledgement, setCurrentAcknowledgment] =
    useState<AtlasGqlAcknowledgement | null>(null);
  const [index, setIndex] = useState(0);

  const [updateAcknowledgement, { loading }] = useUpdateAcknowledgementMutation();

  const numOfAcknowledgements = acknowledgements.length;

  useEffect(() => {
    if (numOfAcknowledgements) {
      setCurrentAcknowledgment(acknowledgements[index]);
    }
  }, [numOfAcknowledgements, acknowledgements, index]);

  const handleAccept = async () => {
    if (!currentAcknowledgement?.id) {
      return;
    }

    await updateAcknowledgement({
      variables: {
        input: {
          id: currentAcknowledgement.id,
          accept: true,
        },
      },
    });
    if (index < numOfAcknowledgements - 1) {
      setIndex(index + 1);
    } else {
      setCurrentAcknowledgment(null);
      setShowModal(false);
    }
  };

  return (
    <Modal
      closable={false}
      keyboard={false}
      centered={true}
      open={showModal}
      destroyOnHidden={true}
      mask={true}
      maskClosable={false}
      footer={
        <>
          <Button type={'primary'} onClick={() => handleAccept()} disabled={loading}>
            {t('common.accept', 'Accept')}
          </Button>
          <Button
            onClick={() => {
              // FIXME: Remove debug after logout problem is solved
              console.debug('AcknowledgementModal: logging out via Decline button');
              logout({ broadcast: true });
            }}
            disabled={loading}
          >
            {t('common.decline', 'Decline')}
          </Button>
        </>
      }
      title={loading ? '' : currentAcknowledgement?.title}
    >
      {loading ? (
        <Spinner />
      ) : (
        <div dangerouslySetInnerHTML={{ __html: currentAcknowledgement?.text ?? '' }} />
      )}
    </Modal>
  );
};
